import React, { useState } from "react";
import {
  Modal,
  Button,
  Radio,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Typography,
} from "@mui/material";
import { styled } from "@mui/system";
import { formatDate, validateEmail } from "utils/miscUtils";
import { SimpleTextInput } from "components/FormTextInput";
import { useSnackbar } from "hooks/useSnackbar";
import FileSaver from "file-saver";

interface ExportModalProps {
  list?: any[];
  fileName?: string;
  filterOptions?: any;
  open?: boolean;
  onClose?: () => void;
  onSendEmail?: (email: any) => void;
}

const StyledModal = styled(Modal)`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ExportModal = ({
  list = [],
  fileName = "data",
  filterOptions = {},
  open = false,
  onClose = () => {},
  onSendEmail = () => {},
}: ExportModalProps) => {
  const { showSnackbar } = useSnackbar();
  const [exportOption, setExportOption] = useState("pageData");
  const [email, setEmail] = useState();

  const saveAsExcelFile = (buffer, fileName) => {
    let EXCEL_TYPE =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
    let EXCEL_EXTENSION = ".xlsx";

    const data = new Blob([buffer], {
      type: EXCEL_TYPE,
    });

    FileSaver.saveAs(
      data,
      fileName + "_export_" + new Date().getTime() + EXCEL_EXTENSION,
    );
  };

  const handleClick = async () => {
    if (!list?.length) {
      showSnackbar("List is empty!");
      return;
    }

    // Lazy load xlsx only when exporting
    const xlsx = await import("xlsx");

    const worksheet = xlsx.utils.json_to_sheet(list);
    const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
    const excelBuffer = xlsx.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    saveAsExcelFile(excelBuffer, fileName);
  };

  const handleExport = async () => {
    if (exportOption === "pageData") {
      // Handle page data export
      await handleClick();
    } else if (exportOption === "sendEmail") {
      if (email === "" || !validateEmail(email)) {
        showSnackbar("Please enter a valid email", "error");
        return;
      }
      onSendEmail(email);
    }
    onClose();
  };

  const onChange = (e: any) => {
    const { value } = e.target;
    setEmail(value);
  };

  return (
    <StyledModal open={open} onClose={onClose}>
      <div
        style={{
          background: "white",
          padding: "20px",
          borderRadius: "8px",
          width: "400px",
        }}
      >
        <h2>Export Options</h2>
        <FormControl component="fieldset">
          <RadioGroup
            value={exportOption}
            onChange={(e) => setExportOption(e.target.value)}
          >
            <FormControlLabel
              value="pageData"
              control={<Radio />}
              label="Export Page Data"
            />
            <FormControlLabel
              value="sendEmail"
              control={<Radio />}
              label={`Send in Email`}
            />
          </RadioGroup>
        </FormControl>
        {exportOption === "sendEmail" && (
          <>
            <SimpleTextInput
              name={"email"}
              label={"Email"}
              onChange={onChange}
              value={email}
              type={"email"}
            />
            <Typography
              variant="body2"
              style={{ marginTop: "10px", fontStyle: "italic" }}
            >
              Note: You can enter multiple comma separated emails like:
              abc@example.com, xyz@example.com
            </Typography>
            <Typography variant="body2" style={{ marginTop: "10px" }}>
              {`Selected Dates:  ${formatDate(
                filterOptions.startTime,
                "dd-MM-yyyy",
              )} to ${formatDate(filterOptions.endTime, "dd-MM-yyyy")}`}
            </Typography>
            <Typography variant="body2" style={{ marginTop: "10px" }}>
              Data will be sent to your email, It may take some time.
            </Typography>
          </>
        )}
        <div style={{ marginTop: "20px", textAlign: "right" }}>
          <Button onClick={onClose} style={{ marginRight: "10px" }}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={handleExport}>
            Export
          </Button>
        </div>
      </div>
    </StyledModal>
  );
};

export default ExportModal;
